import { DataService } from "@/services/data";
import { apiErrorMessage } from "@/utils/api-error";
import { ApprenticeshipRegistrationType } from "@/types/apprenticeship/apprentice";
import { ApprenticeshipAppointmentType, ApprenticeshipInterviewType } from "@/types/apprenticeship/enterprise";
import { PaginatedResponse } from "@/types/common";
import { ProductType } from "@/types/content/product";
import { isFeatureEnabled } from "@/utils/feature-flag";
import { handle_error, handle_success } from "@/utils/wretch";
import { Navigate, RouteObject, redirect } from "react-router-dom";
import { toast } from "sonner";

const CandidateRoute: RouteObject = {
    path: "candidats",
    children: [
        {
            index: true,
            async lazy() {
                let { ListPage } = await import("@/pages/apprenticeship/candidate/list");
                return { Component: ListPage };
            },
            loader: async ({ request }) => {
                const url = new URL(request.url);
                if (!url.searchParams.has("page_size")) {
                    url.searchParams.set("page_size", "25");
                }

                // Normalize legacy/bad multi-values (e.g. user_cap=1,2) to the expected __in lookup
                const multiValueOwnerFilters = ["user_cap", "user_ce", "sales_manager"];
                multiValueOwnerFilters.forEach(filterName => {
                    const rawValue = url.searchParams.get(filterName);
                    if (!rawValue || !rawValue.includes(",") || url.searchParams.has(`${filterName}__in`)) {
                        return;
                    }
                    url.searchParams.set(`${filterName}__in`, rawValue);
                    url.searchParams.delete(filterName);
                });

                const search_param = url.searchParams.get("search");
                if (search_param) {
                    url.searchParams.set("smart_search", search_param);
                    url.searchParams.delete("search");
                }

                const registrations = DataService.client
                    .url(`/apprenticeship/registrations/${url.searchParams ? "?" + url.searchParams.toString() : ""}`)
                    .get()
                    .json();
                return { registrations };
            }
        },
        {
            path: "nouveau",
            action: async ({ request }) => {
                const data = await request.json();
                try {
                    const registration = (await DataService.client.url(`/apprenticeship/registrations/new/`).post(data).json()) as ApprenticeshipRegistrationType;
                    toast.success("Le dossier a été créé");
                    return redirect(`/alternance/candidats/${registration.id}`);
                } catch (error) {
                    toast.error("Une erreur est survenue lors de la création du dossier");
                }
                return redirect("..");
            }
        },
        {
            path: "opportunite",
            action: async ({ request }) => {
                const data = await request.json();
                try {
                    return handle_success(await DataService.client.url(`/apprenticeship/registrations/opportunity/`).post(data).json());
                } catch (error) {
                    return handle_error(error);
                }
            }
        },
        {
            id: "apprenticeship-candidate-detail",
            path: ":registrationId",
            async lazy() {
                let { DetailLayout } = await import("@/pages/apprenticeship/candidate/detail-layout");
                return { Component: DetailLayout };
            },
            loader: async ({ params }) => {
                const registrationId = params.registrationId;
                const registration = (await DataService.client.url(`/apprenticeship/registrations/${registrationId}/`).get().json()) as ApprenticeshipRegistrationType;
                const picklists = await DataService.client
                    .url(`/apprenticeship/registrations/picklists/?contract_type=${registration.apprenticeship_type || "Apprentissage"}`)
                    .get()
                    .json();
                const product = (await DataService.client.url(`/content/products/?product_code=${registration.sf_product?.product_code}`).get().json()) as PaginatedResponse<ProductType>;
                return { registration, picklists, product: product.results[0] };
            },
            action: async ({ params, request }) => {
                const registrationId = params.registrationId;
                const url = new URL(request.url);
                const action = url.searchParams.get("action");

                if (action === "patch_registration_fields") {
                    const data = await request.json();
                    try {
                        const content = await DataService.client.url(`/apprenticeship/registrations/${registrationId}/`).patch(data).json();
                        return handle_success(content);
                    } catch (error) {
                        return handle_error(error);
                    }
                }

                const data = await request.json();
                if (action) {
                    try {
                        const content = await DataService.client.url(`/apprenticeship/registrations/${registrationId}/${action}/`).post(data).json();
                        return { success: true, content: content };
                    } catch (error) {
                        return handle_error(error);
                    }
                }
                return {};
            },
            children: [
                {
                    index: true,
                    element: <Navigate to="dossier" replace />
                },
                {
                    path: "dossier",
                    async lazy() {
                        let { DossierPage } = await import("@/pages/apprenticeship/candidate/detail-dossier");
                        return { Component: DossierPage };
                    },
                    action: async ({ params, request }) => {
                        const registrationId = params.registrationId;
                        const data = await request.json();
                        try {
                            await DataService.client.url(`/apprenticeship/registrations/${registrationId}/`).patch(data).json();
                        } catch (error) {
                            console.error(error);
                            toast.error("Une erreur est survenue lors de l'enregistrement du dossier");
                        }
                        return redirect("..");
                    }
                },
                {
                    loader: async ({ params }) => {
                        const registrationId = params.registrationId;
                        const interviews = await DataService.client.url(`/apprenticeship/interviews/?student_id=${registrationId}&page_size=1000`).get().json();
                        const companiesParams = new URLSearchParams({ candidate_id: registrationId || "0" });
                        if (isFeatureEnabled("apprenticeship-standard-map")) {
                            companiesParams.set("source", "sf");
                        }
                        const companies = DataService.client.url(`/apprenticeship/map/companies/?${companiesParams}`).get().json();
                        return { interviews, companies };
                    },
                    path: "candidatures",
                    async lazy() {
                        let { InterviewsPage } = await import("@/pages/apprenticeship/candidate/detail-interviews");
                        return { Component: InterviewsPage };
                    }
                },
                {
                    path: "rendez-vous",
                    async lazy() {
                        let { AppointmentsPage } = await import("@/pages/apprenticeship/candidate/detail-appointments");
                        return { Component: AppointmentsPage };
                    },
                    loader: async ({ params }) => {
                        const registrationId = params.registrationId;
                        const appointments = await DataService.client.url(`/apprenticeship/appointments/?student_id=${registrationId}&page_size=1000`).get().json();
                        return { appointments };
                    },
                    action: async ({ params, request }) => {
                        const registrationId = params.registrationId;
                        const data = {
                            ...(await request.json()),
                            student: {
                                id: registrationId
                            }
                        };
                        try {
                            const content = await DataService.client.url(`/apprenticeship/appointments/`).post(data).json();
                            return { success: true, content: content };
                        } catch (error) {
                            return handle_error(error);
                        }
                    },
                    children: [
                        {
                            path: ":appointmentId",
                            action: async ({ params, request }) => {
                                const data = await request.json();
                                try {
                                    const content = await DataService.client.url(`/apprenticeship/appointments/${params.appointmentId}/`).patch(data).json();
                                    return { success: true, content: content };
                                } catch (error) {
                                    return handle_error(error);
                                }
                            }
                        }
                    ]
                },
                {
                    path: "cerfa",
                    async lazy() {
                        let { CerfaPage } = await import("@/pages/apprenticeship/candidate/detail-cerfa");
                        return { Component: CerfaPage };
                    },
                    action: async ({ params, request }) => {
                        const registrationId = params.registrationId;
                        const data = await request.json();

                        try {
                            await DataService.client.url(`/apprenticeship/registrations/${registrationId}/`).patch(data).json();
                        } catch (error) {
                            console.error(error);
                            toast.error("Une erreur est survenue lors de l'enregistrement du dossier");
                        }

                        return redirect(".");
                    }
                },
                {
                    path: "suivi-tuteur",
                    async lazy() {
                        let { TutorMonitoringPage } = await import("@/pages/apprenticeship/candidate/detail-tutor-monitoring");
                        return { Component: TutorMonitoringPage };
                    }
                },
                {
                    path: "questionnaires",
                    async lazy() {
                        // TODO: Change
                        let { SurveyPage } = await import("@/pages/apprenticeship/candidate/detail-survey");
                        return { Component: SurveyPage };
                    },
                    loader: async ({ params }) => {
                        const registrationId = params.registrationId;
                        const surveys = await DataService.client.url(`/apprenticeship/surveys/?registration_id=${registrationId}&page_size=1000`).get().json();
                        return { surveys };
                    },
                    action: async ({ params, request }) => {
                        const registrationId = params.registrationId;
                        const url = new URL(request.url);
                        const data = await request.json();
                        if (url.searchParams.get("action")) {
                            try {
                                const response = await DataService.client
                                    .url(`/apprenticeship/surveys/${data.id}/${url.searchParams.get("action")}/`)
                                    .post(data)
                                    .json();
                                return { success: true, content: response };
                            } catch (error) {
                                return handle_error(error);
                            }
                        } else if (request.method === "POST") {
                            try {
                                await DataService.client
                                    .url(`/apprenticeship/surveys/`)
                                    .post({ ...data, registration: registrationId })
                                    .json();
                            } catch (error) {
                                console.error(error);
                                toast.error("Une erreur est survenue lors de la création du questionnaire");
                            }
                        } else {
                            try {
                                await DataService.client
                                    .url(`/apprenticeship/surveys/${data.id}/`)
                                    .put({ ...data, student: registrationId })
                                    .json();
                            } catch (error) {
                                console.error(error);
                                toast.error("Une erreur est survenue lors de la mise à jour du questionnaire");
                            }
                        }
                        return redirect(".");
                    }
                }
            ]
        },
        {
            path: "generate",
            children: [
                {
                    path: ":paymendId",
                    action: async ({ params, request }) => {
                        const paymendId = params.paymendId;
                        const data = await request.json();
                        try {
                            const content = await DataService.client.url(`/apprenticeship/payments/${paymendId}/invoice/`).post(data).json();
                            return { success: true, content: content };
                        } catch (error) {
                            console.error(error);
                            const message = apiErrorMessage(error, "Une erreur est survenue lors de la génération de la facture");
                            toast.error(message);
                            return { success: false, message };
                        }
                    }
                }
            ]
        },
        {
            path: "generate_achievement_certificate",
            children: [
                {
                    path: ":paymendId",
                    action: async ({ params, request }) => {
                        const data = await request.json();
                        const paymendId = params.paymendId;
                        try {
                            const content = await DataService.client.url(`/apprenticeship/payments/${paymendId}/generate_achievement_certificate/`).post(data).json();
                            return { success: true, content: content };
                        } catch (error) {
                            console.error(error);
                            toast.error("Une erreur est survenue lors de la génération du certificat");
                        }
                    }
                }
            ]
        },
        {
            path: "rendez-vous",
            async lazy() {
                let { InterviewCalendarLayout } = await import("@/pages/apprenticeship/candidate/rdv-layout");
                return { Component: InterviewCalendarLayout };
            },
            children: [
                {
                    index: true,
                    element: <Navigate to="calendrier" replace />
                },
                {
                    path: "calendrier",
                    loader: async ({ request }) => {
                        const { DEFAULT_DATE_RANGE } = await import("@/pages/apprenticeship/candidate/rdv-list");
                        const url = new URL(request.url);
                        const eventTypeFilter = url.searchParams.get("event_type__in");

                        // Load interviews
                        const interviewUrl = new URL(request.url);
                        const range = interviewUrl.searchParams.get("datetime__range") || interviewUrl.searchParams.get("interview_datetime__range") || DEFAULT_DATE_RANGE;
                        interviewUrl.searchParams.set("interview_datetime__range", range);
                        interviewUrl.searchParams.delete("datetime__range");
                        interviewUrl.searchParams.set("page_size", "1000");
                        const interviews = (await DataService.client
                            .url(`/apprenticeship/interviews/${interviewUrl.searchParams ? "?" + interviewUrl.searchParams.toString() : ""}`)
                            .get()
                            .json()) as PaginatedResponse<ApprenticeshipInterviewType>;

                        // Load appointments (jury)
                        const appointmentUrl = new URL(request.url);
                        const appRange = appointmentUrl.searchParams.get("datetime__range") || appointmentUrl.searchParams.get("dt_start__range") || DEFAULT_DATE_RANGE;
                        appointmentUrl.searchParams.set("dt_start__range", appRange);
                        appointmentUrl.searchParams.delete("datetime__range");
                        appointmentUrl.searchParams.set("page_size", "1000");

                        const appointments = (await DataService.client
                            .url(`/apprenticeship/appointments/${appointmentUrl.searchParams ? "?" + appointmentUrl.searchParams.toString() : ""}`)
                            .get()
                            .json()) as PaginatedResponse<ApprenticeshipAppointmentType>;

                        // Filter interviews based on event_type__in parameter
                        let filteredInterviews = interviews;
                        if (eventTypeFilter) {
                            const selectedEventTypes = eventTypeFilter.split(",");
                            if (!selectedEventTypes.includes("interview")) {
                                filteredInterviews = { results: [], count: 0, next: null, previous: null } as PaginatedResponse<ApprenticeshipInterviewType>;
                            }
                        }

                        return {
                            interviews: filteredInterviews,
                            appointments: appointments.results.sort((a, b) => new Date(a.dt_start).getTime() - new Date(b.dt_start).getTime())
                        };
                    },
                    async lazy() {
                        let { RdvCalendarPage } = await import("@/pages/apprenticeship/candidate/rdv-calendar");
                        return { Component: RdvCalendarPage };
                    }
                },
                {
                    path: "liste",
                    loader: async ({ request }) => {
                        const { DEFAULT_DATE_RANGE } = await import("@/pages/apprenticeship/candidate/rdv-list");
                        const url = new URL(request.url);
                        const eventTypeFilter = url.searchParams.get("event_type__in");

                        // Load interviews
                        const interviewUrl = new URL(request.url);
                        const range = interviewUrl.searchParams.get("datetime__range") || interviewUrl.searchParams.get("interview_datetime__range") || DEFAULT_DATE_RANGE;
                        interviewUrl.searchParams.set("interview_datetime__range", range);
                        interviewUrl.searchParams.delete("datetime__range");
                        interviewUrl.searchParams.set("page_size", "1000");
                        const interviews = (await DataService.client
                            .url(`/apprenticeship/interviews/${interviewUrl.searchParams ? "?" + interviewUrl.searchParams.toString() : ""}`)
                            .get()
                            .json()) as PaginatedResponse<ApprenticeshipInterviewType>;

                        // Load appointments (jury)
                        const appointmentUrl = new URL(request.url);
                        const appRange = appointmentUrl.searchParams.get("datetime__range") || appointmentUrl.searchParams.get("dt_start__range") || DEFAULT_DATE_RANGE;
                        appointmentUrl.searchParams.set("dt_start__range", appRange);
                        appointmentUrl.searchParams.delete("datetime__range");
                        appointmentUrl.searchParams.set("page_size", "1000");

                        const appointments = (await DataService.client
                            .url(`/apprenticeship/appointments/${appointmentUrl.searchParams ? "?" + appointmentUrl.searchParams.toString() : ""}`)
                            .get()
                            .json()) as PaginatedResponse<ApprenticeshipAppointmentType>;

                        // Filter interviews based on event_type__in parameter
                        let filteredInterviews = interviews;
                        if (eventTypeFilter) {
                            const selectedEventTypes = eventTypeFilter.split(",");
                            if (!selectedEventTypes.includes("interview")) {
                                filteredInterviews = { results: [], count: 0, next: null, previous: null } as PaginatedResponse<ApprenticeshipInterviewType>;
                            }
                        }

                        return {
                            interviews: filteredInterviews,
                            appointments: appointments.results.sort((a, b) => new Date(a.dt_start).getTime() - new Date(b.dt_start).getTime())
                        };
                    },
                    async lazy() {
                        let { RdvListPage } = await import("@/pages/apprenticeship/candidate/rdv-list");
                        return { Component: RdvListPage };
                    }
                }
            ]
        }
    ]
};

export default CandidateRoute;
